import {
  ForgotPasswordScreen,
  loadSession,
  LoginScreen,
  loginSearch,
  MfaEnrollScreen,
  PendingScreen,
  requireMfa,
  requireSession,
  type RouterContext,
  SessionErrorScreen,
  SessionWatcher,
} from '@classgo/app-kit';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
} from '@tanstack/react-router';

import { staffQueries } from '../api/staff-api';
import { ContactsScreen } from '../screens/contacts';
import { TeamScreen } from '../screens/team';
import { TenantDetailScreen } from '../screens/tenant-detail';
import { TenantNewScreen } from '../screens/tenant-new';
import { TenantsScreen } from '../screens/tenants';
import { AppLayout } from '../shell';

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <>
      <SessionWatcher />
      <Outlet />
    </>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: loginSearch,
  component: LoginScreen,
});

const mfaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mfa',
  validateSearch: loginSearch,
  beforeLoad: ({ context, location }) => requireSession(context, location.href),
  component: MfaEnrollScreen,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordScreen,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ context, location }) => requireSession(context, location.href),
  loader: async ({ context, location }) => {
    const session = await loadSession(context, location.href);
    requireMfa(session, location.href);
    return session;
  },
  pendingComponent: PendingScreen,
  errorComponent: SessionErrorScreen,
  component: AppLayout,
});

const tenantsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  loader: ({ context }) => context.queryClient.ensureQueryData(staffQueries.tenants(context.api)),
  component: TenantsScreen,
});

const tenantNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/colegios/nuevo',
  component: TenantNewScreen,
});

const tenantRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/colegios/$tenantId',
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(staffQueries.tenant(context.api, params.tenantId)),
  component: TenantDetailScreen,
});

const contactsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/contactos',
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(staffQueries.contactRequests(context.api)),
  component: ContactsScreen,
});

const teamRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/equipo',
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(staffQueries.staffUsers(context.api)),
  component: TeamScreen,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  mfaRoute,
  forgotPasswordRoute,
  appRoute.addChildren([tenantsRoute, tenantNewRoute, tenantRoute, contactsRoute, teamRoute]),
]);

/**
 * Builds the console's router with the stores it needs.
 *
 * @param {RouterContext} context -- Auth store, API client and query client.
 * @returns The router for `RouterProvider`.
 */
export const createAppRouter = (context: RouterContext) =>
  createRouter({
    routeTree,
    context,
    defaultPreload: 'intent',
    scrollRestoration: true,
    defaultPendingMs: 200,
  });
